/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

/**
 * Enum for sources of the event subscription (excluding special types).
 */
export enum EventSubscriptionSource {
  AdvancedForms = `advancedforms`,
  Datastore = `datastore`,
  Matrix = `matrix`,
}

/**
 * Enum for non-standard sources which do not require an event type for the subscription.
 */
export enum EventSubscriptionSourceSpecial {
  EventScheduler = `event-scheduler`,
}

/**
 * Type for ALL sources of the event subscription. Workaround to extend enum.
 */
export type EventSubscriptionSourceAll = EventSubscriptionSource | EventSubscriptionSourceSpecial;

/**
 * Enum for ALL sources of the event subscription (including special types).
 */
export const EventSubscriptionSourceAll = {
  ...EventSubscriptionSource,
  ...EventSubscriptionSourceSpecial,
};

/**
 * Enum for target types of the event subscription.
 */
export enum EventSubscriptionTargetType {
  Api = `api`,
  Job = 'job',
  IntegrationsFlow = `integrationsFlow`,
}

/**
 * Enum for event types of the event subscription.
 */
export enum EventSubscriptionEventType {
  AssetChangeCreate = 'asset.change.create',
  AssetChangeDelete = 'asset.change.delete',
  AssetChangeUpdate = 'asset.change.update',
  BlueprintChangeCreate = 'blueprint.change.create',
  BlueprintChangeUpdate = 'blueprint.change.update',
  DocumentChangeCreate = 'document.change.create',
  DocumentChangeDelete = 'document.change.delete',
  DocumentChangeUpdate = 'document.change.update',
  FormResponseCreate = 'form.response.create',
}

/**
 * Enum for rule operators of an event attribute filter.
 */
export enum AttributeRule {
  Equals = '=',
  NotEquals = '!=',
  LessThan = '<',
  LessThanEquals = '<=',
  GreaterThan = '>',
  GreaterThanEquals = '>=',
  Exists = 'exists',
  NotExists = 'not-exists',
  BeginsWith = 'begins-with',
  IsNull = 'is-null',
  IsEmpty = 'is-empty',
}

/**
 * The details which represent a single attribute filter for an event subscription.
 */
export interface AttributeFilter {
  id: string;
  name: string;
  rule: AttributeRule;
  value: string;
}

/**
 * Enum for the attributes available to Advanced Forms 'form.response.create' event type.
 */
export enum EventAdvancedFormsFormResponseCreateAttributes {
  formId = `response.formId`,
}

/**
 * Enum for the attributes available to Datastore 'document.change.*' event types.
 */
export enum EventDatastoreDocumentChangeAttributes {
  InstanceId = `instanceID`,
  ServiceUrl = `serviceURL`,
  ChangedValues = `changedValues`,
  Inherited = `inherited`,
  Origin = `origin`,
  Collection = `collection`,
  DocumentId = `documentid`,
  DocumentPath = `documentPath`,
}

/**
 * Enum for the attributes available to Datastore 'blueprint.change.*' event types.
 */
export enum EventDatastoreBlueprintChangeAttributes {
  InstanceId = `instanceID`,
  ServiceUrl = `serviceURL`,
  Origin = `origin`,
  Version = `version`,
}

/**
 * The type of the scheduled job input form (JSON editor).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventScheduledJobInputFormType = Record<string, any>;
